import {
  MemberProductPaymentRatesChargeCode,
  MemberProfile,
  Offering,
} from "@/services/core-api-adapter";
import {
  FeatureToggleIdentifier,
  isFeatureEnabled,
} from "@/services/feature-toggle-adapter";
import theme, { panelStyle } from "@/theme";
import { convertPxToRem, formatAmount } from "@/utils";
import { Box, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { trackEvent, trackUserInteraction } from "@/services/analytics-adapter";
import IconLoader from "@/features/Common/components/IconLoader";
import Panel from "@/features/Common/components/Panel";
import { HealthProfileTile } from "../DashboardTiles";
import LifestyleAndAdviceSection from "../LifestyleAndAdviceSection";
import { TFunction } from "i18next";

interface AdditionalPayGServicesSectionProps {
  memberProfile: MemberProfile | null;
  isMemberProfileLoading: boolean;
  memberProfileFetchError: boolean;
}

interface ServicePanelProps {
  item: any;
  showActiveSessions: boolean;
  t: TFunction;
  onPanelClick: (route: string, name: string) => void;
  getServiceOfferingPriceCTA: (
    type: DashboardServiceOfferingTypes
  ) => React.ReactNode;
  onGetMedicalAssistanceButtonClick: () => void;
}

function getColorByKey(
  key:
    | "DOCTOR_AND_NURSE_CONSULT"
    | "BLOOD_TESTS"
    | "BUY_VOUCHER"
    | "HEALTH_CHECK"
): string {
  const colorMap = {
    DOCTOR_AND_NURSE_CONSULT: theme.palette.primary[700],
    BLOOD_TESTS: theme.palette.error.main,
    BUY_VOUCHER: theme.palette.secondary[900],
    HEALTH_CHECK: theme.palette.accent1[700],
  };

  return colorMap[key] ? colorMap[key].toString() : "";
}

function buildServiceOfferingOptions(
  isPathologyEnabled: boolean,
  isVoucherEnabled: boolean,
  t: TFunction
) {
  return [
    {
      type: DashboardServiceOfferingTypes.DOCTOR_AND_NURSE_CONSULT,
      name: t("PayGMemberDashboard.doctorAndNurseConsult.panel.title"),
      description: t(
        "PayGMemberDashboard.doctorAndNurseConsult.panel.subtitle"
      ),
      route: "/home/get-medical-assistance/intercare",
      leftButton: (
        <Typography fontWeight={600} color="info">
          {t("PayGMemberDashboard.serviceOffering.panel.price", {
            priceFormatted: "R179",
          })}
        </Typography>
      ),
      rightButton: (
        <Typography variant="button" fontWeight={600} color="white">
          {t(
            "PayGMemberDashboard.doctorAndNurseConsult.panel.rightButton.title"
          )}
        </Typography>
      ),
    },
    ...(isPathologyEnabled
      ? [
          {
            type: DashboardServiceOfferingTypes.BLOOD_TESTS,
            name: t("PayGMemberDashboard.bloodTests.panel.title"),
            description: t("PayGMemberDashboard.bloodTests.panel.subtitle"),
            route: "/home/get-medical-assistance/pathology",
            leftButton: (
              <Typography fontWeight={600} color="info">
                {t("PayGMemberDashboard.serviceOffering.panel.price", {
                  priceFormatted: "R184",
                })}
              </Typography>
            ),
            rightButton: (
              <Typography variant="button" fontWeight={600} color="white">
                {t("PayGMemberDashboard.bloodTests.panel.rightButton.title")}
              </Typography>
            ),
          },
        ]
      : []),
    {
      type: DashboardServiceOfferingTypes.HEALTH_CHECK,
      name: t("PayGMemberDashboard.healthCheck.panel.title"),
      description: t("PayGMemberDashboard.healthCheck.panel.subtitle"),
      route: "/binah-scan?skipScanPrompt=true",
      leftButton: (
        <Typography fontWeight={600} color="info">
          {t("common.free")}
        </Typography>
      ),
      rightButton: (
        <Typography variant="button" fontWeight={600} color="white">
          {t("PayGMemberDashboard.healthCheck.panel.rightButton.title")}
        </Typography>
      ),
    },
    ...(isVoucherEnabled
      ? [
          {
            type: DashboardServiceOfferingTypes.BUY_VOUCHER,
            name: t("PayGMemberDashboard.Voucher.panel.title"),
            description: t("PayGMemberDashboard.Voucher.panel.description"),
            route: "/home/get-medical-assistance/vouchers",
            leftButton: (
              <Typography fontWeight={600} color="info">
                {t("PayGMemberDashboard.Voucher.panel.price", {
                  priceFormatted: "R179",
                })}
              </Typography>
            ),
            rightButton: (
              <Typography variant="button" fontWeight={600} color="white">
                {t("PayGMemberDashboard.Voucher.panel.rightButton.title")}
              </Typography>
            ),
          },
        ]
      : []),
  ];
}

function ServicePanel({
  item,
  onPanelClick,
  getServiceOfferingPriceCTA,
  showActiveSessions,
  onGetMedicalAssistanceButtonClick,
  t,
}: ServicePanelProps) {
  const cardBackgroundColor = getColorByKey(item.type);
  return (
    <Stack
      key={item.name}
      sx={{
        ...panelStyle,
        cursor: "pointer",
        border: "none",
        backgroundColor: cardBackgroundColor,
      }}
      component="button"
      role="button"
      aria-label={item.name}
      spacing={1}
      onClick={() =>
        showActiveSessions
          ? onGetMedicalAssistanceButtonClick()
          : onPanelClick(item.route, item.name)
      }
    >
      <Stack
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
      >
        <Typography
          textAlign="left"
          variant="body1"
          fontWeight="bold"
          color="white"
        >
          {item.name}
        </Typography>
        {getServiceOfferingPriceCTA(item.type)}
      </Stack>

      <Stack
        style={{ marginTop: convertPxToRem(9.5) }}
        flexDirection="row"
        alignItems="center"
        width="100%"
      >
        {showActiveSessions ? (
          <Typography fontWeight={600} color="white">
            {t(
              "PayGMemberDashboard.doctorAndNurseConsult.panel.continueSessionButton.title"
            )}
          </Typography>
        ) : (
          item.rightButton
        )}
        <IconLoader icon="ChevronNextIcon" sx={{ color: "white" }} />
      </Stack>

      {showActiveSessions && (
        <Stack
          width="calc(100% + 32px)"
          alignItems="center"
          flexDirection="row"
          sx={{
            ...panelStyle,
            boxShadow: "none",
            borderRadius: 0,
            gap: 1,
            position: "relative",
            left: -16,
            bottom: -16,
          }}
        >
          <IconLoader icon="ClockIcon" sx={{ color: "primary.main" }} />
          <Typography
            fontWeight={600}
            variant="body1"
            color="neutral.dark"
            fontSize={convertPxToRem(14)}
          >
            {t("PayGMemberDashboard.doctorAndNurseConsult.panel.footer.text")}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}

enum DashboardServiceOfferingTypes {
  DOCTOR_AND_NURSE_CONSULT = "DOCTOR_AND_NURSE_CONSULT",
  BLOOD_TESTS = "BLOOD_TESTS",
  HEALTH_CHECK = "HEALTH_CHECK",
  BUY_VOUCHER = "BUY_VOUCHER",
  DOC_MED_BUNDLE = "DOC_MED_BUNDLE",
}

export default function AdditionalPayGServicesSection({
  memberProfile,
  isMemberProfileLoading,
  memberProfileFetchError,
}: AdditionalPayGServicesSectionProps) {
  const isTrendedVitalsEnabled = isFeatureEnabled(
    FeatureToggleIdentifier.ENABLE_TRENDED_VITALS
  );
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [hasActiveSessions, setHasActiveSessions] = useState<boolean>(false);
  const [availableServices, setAvailableServices] = useState<any>({
    consultation: false,
    pathology: false,
    voucher: false,
  });

  useEffect(() => {
    if (memberProfile?.consultation.consultationResponses) {
      setHasActiveSessions(
        memberProfile.consultation.consultationResponses.length > 0
      );
    }
  }, [memberProfile?.consultation.consultationResponses]);

  useEffect(() => {
    if (memberProfile) {
      setAvailableServices({
        consultation: memberProfile.consultation.consultationRates?.length > 0,
        pathology: !!memberProfile.pathology,
        voucher: memberProfile.voucher.rates?.length > 0,
      });
    }
  }, [memberProfile]);

  const isPathologyEnabled = isFeatureEnabled(
    FeatureToggleIdentifier.ENABLE_PATHOLOGY_PANELS
  );

  const isVoucherEnabled = isFeatureEnabled(
    FeatureToggleIdentifier.ENABLE_VOUCHER_REDEMPTION
  );

  const serviceOfferingOptions = useMemo(
    () => buildServiceOfferingOptions(isPathologyEnabled, isVoucherEnabled, t),
    [isPathologyEnabled, isVoucherEnabled]
  );

  function onPanelClick(route: string, name: string) {
    trackUserInteraction({
      linkText: `Home screen | ${name}`,
      linkIntent: "navigational",
      linkScope: "button",
    });

    navigate(route);
  }

  function getServiceOfferingPriceCTA(type: DashboardServiceOfferingTypes) {
    if (type === DashboardServiceOfferingTypes.HEALTH_CHECK) {
      return (
        <Panel
          sx={{
            boxShadow: "none",
            height: "fit-content",
            paddingLeft: convertPxToRem(10),
            paddingRight: convertPxToRem(10),
            paddingTop: convertPxToRem(6),
            paddingBottom: convertPxToRem(6),
          }}
        >
          <Typography variant="body2" fontWeight={600} color="grey[700]">
            {t("common.free")}
          </Typography>
        </Panel>
      );
    } else if (
      type === DashboardServiceOfferingTypes.DOCTOR_AND_NURSE_CONSULT
    ) {
      const isDiscountApplied =
        Boolean(memberProfile?.consultation?.isDiscountApplied) || false;

      const amountFormatted = memberProfile?.consultation.consultationRates
        .find(
          (item) =>
            item.chargeCode ===
            MemberProductPaymentRatesChargeCode.ONLINE_DOCTOR_CONSULTATION
        )
        ?.amountFormatted?.replace(".00", "");

      const originalAmountFormatted =
        memberProfile?.consultation.consultationRates
          .find(
            (item) =>
              item.chargeCode ===
              MemberProductPaymentRatesChargeCode.ONLINE_DOCTOR_CONSULTATION
          )
          ?.originalAmountFormatted?.replace(".00", "");

      return (
        <Panel
          sx={{
            minWidth: isDiscountApplied ? 106 : 82,
            paddingLeft: convertPxToRem(10),
            paddingRight: convertPxToRem(10),
            paddingTop: convertPxToRem(6),
            paddingBottom: convertPxToRem(6),
            ...(isDiscountApplied === true && {
              paddingTop: convertPxToRem(5),
              paddingBottom: convertPxToRem(5),
            }),
            boxShadow: "none",
          }}
        >
          <>
            {!isDiscountApplied ? (
              <Typography variant="body2" fontWeight={600} color="grey[700]">
                {t("PayGMemberDashboard.serviceOffering.panel.price", {
                  priceFormatted: amountFormatted,
                })}
              </Typography>
            ) : (
              <Stack color="info" textAlign="left">
                <Typography variant="body2" fontWeight={600}>
                  {t(
                    "PayGMemberDashboard.serviceOffering.panel.originalAmount.price",
                    {
                      priceFormatted: amountFormatted,
                    }
                  )}
                </Typography>
                <Typography
                  fontWeight={600}
                  variant="body2"
                  sx={{ textDecoration: "line-through" }}
                >
                  {t(
                    "PayGMemberDashboard.serviceOffering.panel.discount.price",
                    {
                      priceFormatted: originalAmountFormatted,
                    }
                  )}
                </Typography>
              </Stack>
            )}
          </>
        </Panel>
      );
    } else if (type === DashboardServiceOfferingTypes.BUY_VOUCHER) {
      const isDiscountApplied =
        Boolean(memberProfile?.voucher?.isDiscountApplied) || false;

      const currencyCode = memberProfile?.voucher?.rates?.find(
        (item) =>
          item.chargeCode ===
          MemberProductPaymentRatesChargeCode.ONLINE_DOCTOR_CONSULTATION
      )?.currencyCode;

      const amountFormatted = memberProfile?.voucher?.rates
        ?.slice()
        .sort((a, b) => {
          if (b.discountAmount !== a.discountAmount) {
            return b.discountAmount - a.discountAmount;
          }
          return a.amount - b.amount;
        })[0]?.amount;

      const originalAmountFormatted = memberProfile?.voucher?.rates?.find(
        (item) =>
          item.chargeCode ===
          MemberProductPaymentRatesChargeCode.ONLINE_DOCTOR_CONSULTATION
      )?.originalAmount;

      return (
        <Panel
          sx={{
            minWidth: 82,
            paddingLeft: convertPxToRem(10),
            paddingRight: convertPxToRem(10),
            paddingTop: convertPxToRem(6),
            paddingBottom: convertPxToRem(6),
            ...(isDiscountApplied === true && {
              paddingTop: convertPxToRem(5),
              paddingBottom: convertPxToRem(5),
            }),
            boxShadow: "none",
          }}
        >
          <>
            {!isDiscountApplied ? (
              <Typography variant="body2" fontWeight={600} color="grey[700]">
                {t("PayGMemberDashboard.serviceOffering.panel.price", {
                  priceFormatted: formatAmount(
                    amountFormatted || 0,
                    currencyCode || ""
                  ),
                })}
              </Typography>
            ) : (
              <Stack color="info" textAlign="left">
                <Typography variant="body2" fontWeight={600} color="info">
                  {t(
                    "PayGMemberDashboard.serviceOffering.panel.originalAmount.price",
                    {
                      priceFormatted: formatAmount(
                        amountFormatted || 0,
                        currencyCode || ""
                      ),
                    }
                  )}
                </Typography>
                <Typography
                  fontWeight={600}
                  color="info"
                  variant="body2"
                  sx={{ textDecoration: "line-through" }}
                >
                  {t(
                    "PayGMemberDashboard.serviceOffering.panel.discount.price",
                    {
                      priceFormatted: formatAmount(
                        originalAmountFormatted || 0,
                        currencyCode || ""
                      ),
                    }
                  )}
                </Typography>
              </Stack>
            )}
          </>
        </Panel>
      );
    } else if (type === DashboardServiceOfferingTypes.DOC_MED_BUNDLE) {
      memberProfile?.offerings.map((offering: Offering) => {
        const isDiscountApplied = Boolean(offering?.isDiscountApplied) || false;
        const amountFormatted = formatAmount(
          offering?.amount || 0,
          offering.currencyCode || ""
        );
        const originalAmountFormatted = formatAmount(
          offering.originalAmount || 0,
          offering.currencyCode || ""
        );
        return (
          <Panel
            sx={{
              minWidth: 82,
              paddingLeft: convertPxToRem(10),
              paddingRight: convertPxToRem(10),
              paddingTop: convertPxToRem(6),
              paddingBottom: convertPxToRem(6),
              ...(isDiscountApplied === true && {
                paddingTop: convertPxToRem(5),
                paddingBottom: convertPxToRem(5),
              }),
              boxShadow: "none",
            }}
          >
            <>
              {!isDiscountApplied ? (
                <Typography variant="body2" fontWeight={600} color="info">
                  {t(
                    "PayGMemberDashboard.FeaturedServiceOffering.panel.price",
                    {
                      priceFormatted: amountFormatted,
                    }
                  )}
                </Typography>
              ) : (
                <Stack color="info" textAlign="left">
                  <Typography variant="body2" fontWeight={600} color="info">
                    {t(
                      "PayGMemberDashboard.FeaturedServiceOffering.panel.originalAmount.price",
                      {
                        priceFormatted: amountFormatted,
                      }
                    )}
                  </Typography>
                  <Typography
                    fontWeight={600}
                    color="info"
                    variant="body2"
                    sx={{ textDecoration: "line-through" }}
                  >
                    {t(
                      "PayGMemberDashboard.FeaturedServiceOffering.panel.discount.price",
                      {
                        priceFormatted: originalAmountFormatted,
                      }
                    )}
                  </Typography>
                </Stack>
              )}
            </>
          </Panel>
        );
      });
    } else if (type === DashboardServiceOfferingTypes.BLOOD_TESTS) {
      const isDiscountApplied =
        Boolean(memberProfile?.pathology?.isDiscountApplied) || false;

      const amountFormatted = formatAmount(
        memberProfile?.pathology?.amount || 0,
        memberProfile?.pathology?.currencyCode || ""
      );

      const originalAmountFormatted = formatAmount(
        memberProfile?.pathology?.originalAmount || 0,
        memberProfile?.pathology?.currencyCode || ""
      );

      const pathologyAmount = memberProfile?.pathology?.amount || 0;
      const amountWidth = pathologyAmount > 9900 ? 82 : 75;

      return (
        <Panel
          sx={{
            minWidth: amountWidth,
            paddingLeft: convertPxToRem(10),
            paddingRight: convertPxToRem(10),
            paddingTop: convertPxToRem(6),
            paddingBottom: convertPxToRem(6),
            ...(isDiscountApplied === true && {
              paddingTop: convertPxToRem(5),
              paddingBottom: convertPxToRem(5),
            }),
            boxShadow: "none",
          }}
        >
          <>
            {!isDiscountApplied ? (
              <Typography variant="body2" fontWeight={600} color="grey[700]">
                {t("PayGMemberDashboard.serviceOffering.panel.price", {
                  priceFormatted: amountFormatted,
                })}
              </Typography>
            ) : (
              <Stack color="info" textAlign="left">
                <Typography variant="body2" fontWeight={600} color="info">
                  {t(
                    "PayGMemberDashboard.serviceOffering.panel.originalAmount.price",
                    {
                      priceFormatted: amountFormatted,
                    }
                  )}
                </Typography>
                <Typography
                  fontWeight={600}
                  color="info"
                  variant="body2"
                  sx={{ textDecoration: "line-through" }}
                >
                  {t(
                    "PayGMemberDashboard.serviceOffering.panel.discount.price",
                    {
                      priceFormatted: originalAmountFormatted,
                    }
                  )}
                </Typography>
              </Stack>
            )}
          </>
        </Panel>
      );
    }
  }

  return (
    <Stack spacing={2}>
      <Stack sx={{ zIndex: 2 }} spacing={2}>
        <Stack spacing={2}>
          {serviceOfferingOptions.map((item, index) => {
            const isDoctorAndNurseConsult =
              item.type ===
              DashboardServiceOfferingTypes.DOCTOR_AND_NURSE_CONSULT;

            const hasActiveSessionsAndUnifiedCareEnabled =
              hasActiveSessions &&
              isFeatureEnabled(FeatureToggleIdentifier.ENABLE_UNIFIED_CARE);

            if (
              item.type ===
                DashboardServiceOfferingTypes.DOCTOR_AND_NURSE_CONSULT &&
              !availableServices.consultation
            ) {
              return null;
            }

            if (
              item.type === DashboardServiceOfferingTypes.BLOOD_TESTS &&
              !availableServices.pathology
            ) {
              return null;
            }

            if (
              item.type === DashboardServiceOfferingTypes.BUY_VOUCHER &&
              !availableServices.voucher
            ) {
              return null;
            }

            return (
              <ServicePanel
                key={index}
                item={item}
                t={t}
                showActiveSessions={
                  isDoctorAndNurseConsult &&
                  hasActiveSessionsAndUnifiedCareEnabled
                }
                onPanelClick={onPanelClick}
                getServiceOfferingPriceCTA={getServiceOfferingPriceCTA}
                onGetMedicalAssistanceButtonClick={() => {
                  trackEvent({
                    event: "action.getMedicalAssistanceInitiated",
                    "getMedicalAssistance.source": "Home screen",
                  });

                  trackUserInteraction({
                    linkText: "Home screen | getMedicalAssistanceInitiated",
                    linkIntent: "navigational",
                    linkScope: "button",
                  });

                  navigate("/home/get-medical-assistance/intercare");
                }}
              />
            );
          })}
        </Stack>
        {!isTrendedVitalsEnabled && (
          <Stack spacing={2}>
            <Stack
              justifyContent="space-between"
              flexDirection="row"
              alignItems="center"
            >
              <Typography component="h2" fontWeight={600}>
                {t("common.healthInfo")}
              </Typography>
              <Box
                sx={{
                  p: 1,
                  background: theme.palette.accent1.main,
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color="accent1.contrastText"
                >
                  {t("common.freeInfo")}
                </Typography>
              </Box>
            </Stack>

            <HealthProfileTile
              lastUpdatedDate={memberProfile?.healthProfile?.lastUpdatedAt}
              healthScore={
                Number(memberProfile?.healthProfile?.healthScoreValue) || 0
              }
              isLoading={isMemberProfileLoading}
              isError={memberProfileFetchError}
              fullWidth
            />

            {isFeatureEnabled(FeatureToggleIdentifier.ENABLE_CHAT_BOT) && (
              <LifestyleAndAdviceSection
                displayTitle={false}
                displayTag={false}
              />
            )}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
